/* eslint-disable react-refresh/only-export-components */
// main.tsx is the app ENTRY module: it runs side-effects (initSentry, createRoot)
// and legitimately defines route components (the React.lazy() consts + AppContent)
// that are never HMR-swapped — Fast Refresh does not apply to the root. Disabling
// the rule file-wide here is the idiomatic treatment for an entry point (mirrors
// the documented eslint-disable convention used in auth/AuthProvider.tsx).
// Phase 31.6 FE-31-11.
import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import './index.css'
import { initSentry } from './lib/sentry'
// Phase — stale-chunk-post-deploy auto-recovery (Sentry SWEATSTUDIO-REACT-W).
// lazyWithReload wraps each split-point so a client on an old build hard-reloads
// once to fetch the current chunks instead of dead-ending on the ErrorBoundary.
import { lazyWithReload as lazy } from './lib/lazyReload'
import { AuthProvider } from './auth/AuthProvider'
import { LanguageProvider } from './i18n/LanguageProvider'
import { langFromPath, EN_PREFIX } from './i18n/urlLang'

// Phase 18 LAUNCH-14 — initialize Sentry early (before React mounts).
// Kill-switch: VITE_SENTRY_ENABLED=false → Sentry.init is never called.
initSentry()

// EAGER (D-03 critical path — stay in the main chunk):
import Home from './pages/Home'
import Login from './pages/Login'
import AuthCallback from './pages/AuthCallback'
// Guards stay eager (tiny — the lazy page is the element INSIDE the guard, so
// the guard renders first and the dynamic import only fires when it passes).
import ProtectedRoute from './components/ProtectedRoute'
import AdminRoute from './components/AdminRoute'
// Root-render helpers (eager):
import InviteTokenRedirect from './components/InviteTokenRedirect'
import RouteSkeleton from './components/RouteSkeleton'
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback'
import ScrollToHash from './components/ScrollToHash'
// Phase 96 — catch-all `*` (il n'en existait AUCUNE : un chemin inconnu rendait
// le vide, servi en 200 par le repli SPA de Cloudflare Pages) et balises `<head>`
// de la page courante. Les deux restent EAGER : `NotFound` ne porte que Header +
// Footer, déjà dans le chunk d'entrée par Home, et `SeoHead` se monte sur
// toutes les pages — le charger en différé ferait émettre le canonical une
// frame trop tard, pour rien.
import NotFound from './components/NotFound'
import SeoHead from './seo/SeoHead'

// LAZY (Phase 31.6 D-03 / FE-31-15) — Vite/Rollup auto-splits each dynamic
// import() into its own chunk; NO vite.config manualChunks needed (Pitfall 1).
// All pages are `export default`, so no `.then(m => ({default: m.X}))` re-map.
//
// Phase 99 (D-02) — `MentionsLegales` était la SEULE page hors chemin critique
// à rester EAGER : elle voyageait dans le chunk d'entrée avec Home, Login et
// AuthCallback. Depuis la Phase 99 elle porte sa propre prose légale
// (`pages/legal/fr.ts`, ~13 200 caractères), et un import statique l'aurait
// déversée telle quelle dans le fichier que TOUT visiteur francophone
// télécharge — exactement l'échange que D-02 refuse. Sortir la prose du
// catalogue partagé sans ce geste-ci n'aurait déplacé le poids que d'un fichier
// à l'autre, à l'intérieur du même chunk : D-02 respectée à la lettre et trahie
// sur le fond. La route est déjà sous <Suspense>, donc le basculement ne demande
// aucun échafaudage. Refs: phase 99.
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Packs = lazy(() => import('./pages/Packs'))
const PackCheckout = lazy(() => import('./pages/PackCheckout'))
const PurchaseSuccess = lazy(() => import('./pages/PurchaseSuccess'))
const ReserveSession = lazy(() => import('./pages/ReserveSession'))
const ReserveTrial = lazy(() => import('./pages/ReserveTrial'))
const ReserveCoachingPerso = lazy(() => import('./pages/ReserveCoachingPerso'))
// Phase 73 BLOG-01 — public blog (no ProtectedRoute). Lazy so the TipTap admin
// chunk (73-03) never lands in these public bundles; here they carry DOMPurify only.
const Blog = lazy(() => import('./pages/Blog'))
const BlogArticle = lazy(() => import('./pages/BlogArticle'))
const Feedback = lazy(() => import('./pages/Feedback'))
const MonCompte = lazy(() => import('./pages/MonCompte'))
// Phase 109 (FACT-01) — page dédiée aux factures. Le chargement différé n'est
// pas décoratif ici : c'est lui qui garde le catalogue page-local
// `pages/billing/{fr,en}.ts` hors du chunk d'entrée (D-02, comme `pages/legal/`).
const MesFactures = lazy(() => import('./pages/MesFactures'))
const SessionCheckout = lazy(() => import('./pages/SessionCheckout'))
const SessionPurchaseSuccess = lazy(() => import('./pages/SessionPurchaseSuccess'))
const Install = lazy(() => import('./pages/Install'))
const SlotConfirmation = lazy(() => import('./pages/SlotConfirmation'))
const AdminLayout = lazy(() => import('./components/AdminLayout'))
const AdminPage = lazy(() => import('./pages/AdminPage'))
const AdminReservations = lazy(() => import('./pages/admin/AdminReservations'))
const AdminUtilisateurs = lazy(() => import('./pages/admin/AdminUtilisateurs'))
const AdminCodesPromo = lazy(() => import('./pages/admin/AdminCodesPromo'))
// Phase 73 BLOG-02 — admin blog CRUD. Lazy so TipTap (BlogEditor) lands in this
// admin chunk, never the public /blog bundle.
const AdminBlog = lazy(() => import('./pages/admin/AdminBlog'))
const AdminAvis = lazy(() => import('./pages/admin/AdminAvis'))
const AdminSuivi = lazy(() => import('./pages/admin/AdminSuivi'))
const AdminStats = lazy(() => import('./pages/admin/AdminStats'))
const AdminRevenus = lazy(() => import('./pages/admin/AdminRevenus'))
const AdminAvailability = lazy(() => import('./pages/admin/AdminAvailability'))
const AdminIntegrations = lazy(() => import('./pages/admin/AdminIntegrations'))
// Phase 49 F1 (D-01) — admin-invite acceptance route. TOP-LEVEL (NOT under
// <AdminRoute> guard) because the user has no session at click-time; the
// new Edge Function `accept-admin-invite` validates the magic-link token
// + grants the role, then this page navigates to /admin where the
// standard guard resumes.
const AdminAcceptInvite = lazy(() => import('./pages/admin/AdminAcceptInvite'))
// Phase 111 (APERCU-04) — aperçu en lecture seule du compte d'un client. Route
// ENFANT de /admin, donc sous <AdminRoute> ET sous `routerBase === '/'` :
// l'administration reste française (D-08). AdminRoute n'est qu'un confort de
// navigation — la vraie frontière est la garde `is_admin()` en base (plan 111-01),
// comme le dit déjà le commentaire du bloc /admin ci-dessous.
const AdminApercuCompte = lazy(() => import('./pages/admin/AdminApercuCompte'))

// Phase 96 (D-01/D-03/I18N-04) — préfixe de langue. `/en/packs` est servi par
// la MÊME table de routes que `/packs` : c'est le `basename` du routeur qui
// retire le préfixe avant le matching, et qui le remet sur les 46 `<Link>`, 12
// `navigate()` et 9 `<Navigate>` en chemins absolus du dépôt. AUCUN de ces 67
// sites d'appel n'est à convertir, et le prochain écrit dans six mois héritera
// du préfixe sans que personne n'y pense — c'est toute la raison du choix.
//
// ⚠️ CHEMIN CRITIQUE. Le mode de panne d'un mauvais calcul n'est PAS une page
// cassée : une URL qui ne commence pas par le `basename` fait rendre le VIDE à
// <Routes>, donc l'application ENTIÈREMENT blanche. Le cas d'école est le
// `startsWith` sans barre finale, qui déclarerait `/enveloppe` anglaise, ferait
// chercher `/veloppe` au routeur et blanchirait le site. La garde exacte vit
// dans i18n/urlLang.ts (comparaison `=== '/en'` ou `startsWith('/en/')`), elle
// y est testée sur `/enveloppe` et `/entrainement`, et rien ici ne doit la
// ré-implémenter ni la « simplifier ».
//
// Lu une seule fois au démarrage : la langue d'un document ne change pas en
// cours de session (D-05 — la bascule est une navigation document).
const routerBase = langFromPath(window.location.pathname) ? EN_PREFIX : '/'

// AppContent — rendered INSIDE <BrowserRouter> so useLocation() is available.
// Reset-on-route-change (Gemini Gate 1 finding #1): the root Sentry.ErrorBoundary
// must RESET on route change so navigating away after an error (Back button /
// link) clears the fallback instead of staying stuck.
//
// DEVIATION (Rule 3): the plan prescribed `resetKeys={[location.pathname]}`, but
// @sentry/react@10.52.0's ErrorBoundary has NO `resetKeys` prop (that is a
// react-error-boundary API). The supported, idiomatic React equivalent is
// `key={location.pathname}` — when the key changes, React unmounts+remounts the
// boundary, clearing its error state. Confirmed correct + idiomatic via Gemini
// Gate 5 (boundary is scoped to <Routes>; AuthProvider/BrowserRouter/
// InviteTokenRedirect stay OUTSIDE the keyed subtree, so no persistent state is
// lost — pages already remount on navigation).
// Exporté depuis la Phase 111 pour que l'ARBRE DE ROUTES soit testable. Sans
// cet export, `/admin/apercu/:userId` n'était prouvée nulle part : le test de
// rendu de la page re-déclarait sa propre route en mémoire, donc il prouvait le
// composant et jamais le câblage. Supprimer la ligne `<Route path="apercu/…">`
// ci-dessous laissait toute la suite verte.
export function AppContent() {
  const location = useLocation()
  return (
    <>
      <ScrollToHash />
      {/* Phase 96 (T-96-13) — UNE SEULE instance de SeoHead dans toute
          l'application. React ne déduplique PAS les balises de `<head>` : un
          second montage produirait deux `<link rel="canonical">`, même avec un
          href identique, donc deux signaux contradictoires envoyés aux moteurs
          sans la moindre erreur visible. Elle est posée ICI, en frère de
          ScrollToHash et HORS du Sentry.ErrorBoundary keyé sur le chemin (qui
          remonte à chaque navigation — l'y mettre la démonterait/remonterait
          pour rien). Une page ajoutée plus tard hérite donc du SEO sans que
          personne n'y pense : il n'y a qu'une ligne à ajouter à seo/routes.ts. */}
      <SeoHead />
      <InviteTokenRedirect />
      <Sentry.ErrorBoundary
        key={location.pathname}
        fallback={(props) => <ErrorBoundaryFallback {...props} />}
      >
        <Suspense fallback={<RouteSkeleton />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/packs" element={<Packs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            {/* Phase 75 — publique par conception : le token opaque de l'URL est
                la seule chose que l'utilisateur présente (décision produit 3). */}
            <Route path="/feedback/:token" element={<Feedback />} />
            <Route
              path="/packs/checkout/:size"
              element={
                <ProtectedRoute>
                  <PackCheckout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/purchase/success"
              element={
                <ProtectedRoute>
                  <PurchaseSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reserve/:duration_hours"
              element={
                <ProtectedRoute>
                  <ReserveSession />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reserve/trial"
              element={
                <ProtectedRoute>
                  <ReserveTrial />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reserve/coaching-perso"
              element={
                <ProtectedRoute>
                  <ReserveCoachingPerso />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mon-compte"
              element={
                <ProtectedRoute>
                  <MonCompte />
                </ProtectedRoute>
              }
            />
            {/* Phase 109 (FACT-01, T-109-24) — protégée comme /mon-compte : la
                garde de route n'est qu'une commodité, la vraie garde reste la
                RLS côté base. Posée AVANT le bloc `routerBase === '/'` et AVANT
                le catch-all `*` — une route posée après le catch-all n'est
                jamais atteinte, et rien ne le signale. */}
            <Route
              path="/mes-factures"
              element={
                <ProtectedRoute>
                  <MesFactures />
                </ProtectedRoute>
              }
            />
            <Route path="/seance-unitaire" element={<Navigate to="/session/checkout" replace />} />
            <Route path="/session/checkout" element={<SessionCheckout />} />
            <Route
              path="/session/purchase/success"
              element={
                <SessionPurchaseSuccess />
              }
            />
            <Route path="/install" element={<Install />} />
            <Route
              path="/booking/confirmed/:id"
              element={
                <ProtectedRoute>
                  <SlotConfirmation />
                </ProtectedRoute>
              }
            />
            {/* Phase 96 (D-08) — l'administration reste FRANÇAISE. La table
                étant partagée, sans cette condition `/en/admin/reservations`
                matcherait et rendrait l'administration à une URL anglaise. Les
                DEUX entrées admin sont concernées, pas seulement le parent.
                Ce n'est PAS un contrôle d'accès : AdminRoute et les RLS serveur
                restent les seules gardes réelles — ici on ne fait que ne pas
                servir une jumelle qui n'a aucune raison d'exister. Sous
                `basename="/en"`, le catch-all `*` prend le relais et NotFound
                sort de l'arbre anglais par une navigation document. */}
            {routerBase === '/' && (
              <>
                {/* Phase 49 F1 (D-01) — admin-invite acceptance, TOP-LEVEL (no
                    AdminRoute guard — user has no session yet at click-time).
                    Registered BEFORE the /admin parent so the longer path matches
                    first in react-router-dom@7. */}
                <Route path="/admin/accept-invite" element={<AdminAcceptInvite />} />
                <Route
                  path="/admin"
                  element={
                    <AdminRoute>
                      <AdminLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/reservations" replace />} />
                  <Route path="reservations" element={<AdminReservations />} />
                  <Route path="availability" element={<AdminAvailability />} />
                  <Route path="utilisateurs" element={<AdminUtilisateurs />} />
                  <Route path="suivi" element={<AdminSuivi />} />
                  <Route path="apercu/:userId" element={<AdminApercuCompte />} />
                  <Route path="equipe" element={<AdminPage />} />
                  <Route path="codes-promo" element={<AdminCodesPromo />} />
                  <Route path="blog" element={<AdminBlog />} />
                  <Route path="avis" element={<AdminAvis />} />
                  <Route path="integrations" element={<AdminIntegrations />} />
                  <Route path="revenus" element={<AdminRevenus />} />
                  <Route path="statistiques" element={<AdminStats />} />
                </Route>
              </>
            )}
            {/* Phase 96 — catch-all, EN DERNIER et pour les DEUX arbres (la
                table est partagée). Avant lui, `/nimportequoi` rendait le vide
                en 200, sans en-tête ni exclusion d'indexation. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Sentry.ErrorBoundary>
    </>
  )
}

// Ordre imposé (Phase 95) : AuthProvider DEHORS, LanguageProvider DEDANS.
// LanguageProvider appelle useAuth() pour la réconciliation, et
// auth/AuthProvider.tsx lève `useAuth must be used within AuthProvider` quand
// le contexte manque — l'inverser rendrait un écran blanc sur toutes les pages.
// LanguageProvider reste AU-DESSUS de BrowserRouter pour survivre à la
// navigation.
// Le bootstrap ne s'exécute QUE s'il a une cible. Sans cette garde, importer ce
// module depuis un test monterait l'application entière (ou jetterait sur un
// `#root` absent) — c'est ce qui rendait l'arbre de routes intestable.
const rootElement = document.getElementById('root')
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter basename={routerBase}>
            <AppContent />
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </StrictMode>,
  )
}
